import { createRepository } from '@jobcost-monorepo/crud/dto/repositoryFactory';
import {
  IJob,
  IListJobFetchParams,
  ListContext,
  MN_JOBS,
  Paths,
  useList,
} from '@jobcost-monorepo/crud';
import React, { useEffect, useState } from 'react';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
} from '@jobcost-monorepo/ui';
import CrudForm from '@jobcost-monorepo/crud/ui/CrudForm';
import { JobStateEnum } from '@jobcost-monorepo/database/prisma/enums';

interface Props {
  isExpired?: boolean;
  isExpiring?: boolean;
}

const WidgetJobs = ({ isExpired, isExpiring }: Props) => {
  const { t } = useTranslation(['jobs', 'commons']);
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const repository = createRepository<IJob, IListJobFetchParams>(MN_JOBS);
  const labelNoData = t('noData', { ns: 'commons' });
  const labelJobs = t('widget.expireWidget.jobs');
  const labelExpired = t('widget.expireWidget.expired');
  const labelExpiring = t('widget.expireWidget.expiring');

  const {
    fetchDataList,
    setFormElement,
    setParams,
    dataList,
    deleteElement,
    formElement,
    params,
    loading,
  } = useList<IJob, IListJobFetchParams>({
    moduleName: MN_JOBS,
    repository,
    updateStore: false,
    forcedParams: {
      isExpired,
      isExpiring,
      state: JobStateEnum.Running,
      take: 100,
    } as Record<Paths<IListJobFetchParams>, any>,
  });

  const handleEdit = (element: IJob) => {
    setFormElement(element);
    setFormOpen(true);
  };

  useEffect(() => {
    fetchDataList().then();
  }, []);

  return (
    <ListContext.Provider
      value={{
        fetchDataList,
        setFormElement,
        setParams,
        dataList,
        deleteElement,
        formElement,
        params,
        loading,
        setFormOpen,
        setDeleteModalOpen: () => void 0,
        moduleName: MN_JOBS,
        repository,
      }}
    >
      <div className="sm:max-w-[100%] md:min-w-[400px] h-[400px] flex-1 p-4 flex-col overflow-y-auto">
        <div className="rounded-lg border-purple70 dark:border-grey border-[1px] size-full overflow-y-auto">
          <div className="flex flex-row py-2 px-2 font-bold sticky top-0 bg-white dark:bg-darkGrey z-10 justify-between">
            <div>{`${labelJobs} ${isExpired ? labelExpired : labelExpiring}`}</div>
            <div
              className={`${
                dataList.data.length === 0 ? 'hidden' : 'flex'
              } items-center justify-center ml-2 bg-violet40 dark:bg-black rounded-full h-6 w-6 text-sm`}
            >
              {dataList.data.length}
            </div>
          </div>
          {dataList.data.length === 0 && (
            <div className="h-20 flex flex-col px-2">{labelNoData}</div>
          )}
          {dataList.data.map((item, index) => {
            return (
              <div
                key={`job-${item.id}-isExpired[${isExpired}]-isExpiring[${isExpiring}]`}
                className={`py-2 px-2 flex ${
                  index % 2
                    ? 'bg-violet5 dark:bg-darkGrey'
                    : 'bg-violet2 dark:bg-transparent'
                }`}
              >
                <div className="mr-4 min-w-fit">
                  {dayjs.utc(item.endDate).format('DD-MM-YYYY')}
                </div>
                <div className="flex-col w-full">
                  <div className="font-bold flex flex-1 justify-between">
                    <div className="font-bold">{item.customer.name}</div>
                    <div
                      onClick={() => handleEdit(item)}
                      className="hover:cursor-pointer"
                    >
                      <Icon
                        iconName="edit"
                        className="hover:opacity-50 fill-black stroke-black dark:fill-white dark:stroke-white"
                      />
                    </div>
                  </div>
                  <div className="flex-col">
                    <div>{item.code}</div>
                    <div>{item.description}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Dialog open={formOpen} onOpenChange={() => setFormOpen(false)}>
          <DialogTrigger asChild className="w-full"></DialogTrigger>
          <DialogContent
            hasCloseIcon={false}
            className="relative transform overflow-y-hidden rounded-lg mx-5 border border-gray dark:border-lightGray bg-white dark:bg-dark px-4 pt-5 pb-4 text-left shadow-xl transition-all w-full"
          >
            <CrudForm<IJob, IListJobFetchParams> />
          </DialogContent>
        </Dialog>
      </div>
    </ListContext.Provider>
  );
};

export default WidgetJobs;
